/**
 * Phase 7 Slice D.2 — WebSocket context provider.
 *
 * Mounted once in AppLayout, owns the per-tenant ``/ws/cockpit`` socket
 * and exposes:
 *   - ``status`` — "idle"|"connecting"|"open"|"closed"|"error"
 *   - ``notifTick`` — counter that bumps each time a ``notification`` arrives
 *   - ``oauthTick`` — counter that bumps on ``oauth.refreshed``
 *
 * Cockpit-affecting events are dispatched directly to the tanstack-query
 * client via ``queryClient.invalidateQueries({ queryKey: [...] })`` so
 * any open cockpit page refetches without a refresh.
 */
import React, { createContext, useContext, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTenantWebSocket } from "../lib/useTenantWebSocket";

const WSContext = createContext({
  status: "idle", notifTick: 0, oauthTick: 0,
});

export function WSProvider({ children }) {
  const qc = useQueryClient();
  const [notifTick, setNotifTick] = useState(0);
  const [oauthTick, setOauthTick] = useState(0);

  const onMessage = useCallback((msg) => {
    if (!msg || !msg.type) return;
    switch (msg.type) {
      case "notification":
        setNotifTick((v) => v + 1);
        break;
      case "cockpit.invalidate":
        // Invalidate every cockpit-shaped query; tanstack will refetch only
        // queries actually mounted right now.
        qc.invalidateQueries({ queryKey: ["cockpit-summary"] });
        qc.invalidateQueries({ queryKey: ["ad-spend-by-day"] });
        qc.invalidateQueries({ queryKey: ["revenue-events"] });
        break;
      case "oauth.refreshed":
        setOauthTick((v) => v + 1);
        qc.invalidateQueries({ queryKey: ["crm-connection"] });
        break;
      default:
        break;
    }
  }, [qc]);

  const status = useTenantWebSocket(onMessage);

  return (
    <WSContext.Provider value={{ status, notifTick, oauthTick }}>
      {children}
    </WSContext.Provider>
  );
}

export function useWS() {
  return useContext(WSContext);
}
